import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { RiCameraLine, RiShieldCheckLine, RiMapPinLine, RiQuestionLine, RiArrowRightSLine, RiFileTextLine } from "@remixicon/react";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import { getUser, updateUser, uploadProfilePic } from "../../api/auth";
import { setUser } from "../../store/authSlice";
import { formatMoney } from "../../utils/status";
import avatar from "../../Assets/images/no-profile-avatar.jpg";

const LINKS = [
	{ to: "/company-verification", icon: RiShieldCheckLine, label: "Business verification", text: "Company details, owners and documents" },
	{ to: "/locations", icon: RiMapPinLine, label: "Manage delivery locations", text: "Add, edit or remove delivery sites" },
	{ to: "/help", icon: RiQuestionLine, label: "Get help & feedback", text: "Contact Ordr support" },
	{ to: "/terms-of-service", icon: RiFileTextLine, label: "Terms of service", text: "" },
	{ to: "/privacy-policy", icon: RiFileTextLine, label: "Privacy policy", text: "" },
];

export default function Settings() {
	const dispatch = useDispatch();
	const storedUser = useSelector((s) => s.auth.user);
	const fileRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [form, setForm] = useState({ firstName: "", lastName: "" });
	const [saving, setSaving] = useState(false);
	const [uploading, setUploading] = useState(false);
	const [profile, setProfile] = useState(null);

	const loadProfile = async () => {
		const res = await getUser();
		if (res && !res.error && res.data) {
			setProfile(res.data);
			setForm({ firstName: res.data.firstName || "", lastName: res.data.lastName || "" });
			return res.data;
		}
		return null;
	};

	useEffect(() => {
		(async () => {
			setLoading(true);
			await loadProfile();
			setLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const save = async () => {
		setSaving(true);
		const res = await updateUser(form);
		setSaving(false);
		if (res && !res.error) {
			toast.success("Profile updated");
			dispatch(setUser({ ...storedUser, ...form }));
		} else {
			toast.error(res?.msg || "Could not update profile");
		}
	};

	// Mirrors ordr-flutter profile_bloc -> repository.uploadProfilePic (multipart field "usersPic")
	const onPickPhoto = async (e) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file) return;
		if (!file.type.startsWith("image/")) {
			toast.error("Please choose an image file");
			return;
		}
		const formData = new FormData();
		formData.append("usersPic", file);
		setUploading(true);
		const res = await uploadProfilePic(formData);
		if (res && !res.error) {
			const fresh = await loadProfile();
			if (fresh) dispatch(setUser({ ...storedUser, ...fresh }));
			toast.success("Profile photo updated");
		} else {
			toast.error(res?.msg || "Could not upload photo");
		}
		setUploading(false);
	};

	if (loading) return <Loader />;

	const dirty = form.firstName !== (profile?.firstName || "") || form.lastName !== (profile?.lastName || "");

	return (
		<div>
			<PageHeader title="Account settings" subtitle="Manage your profile, company and preferences" />
			<div className="row g-4">
				<div className="col-xl-7">
					<div className="card p-4 mb-4">
						<div className="d-flex align-items-center gap-3 mb-4">
							<div className="profile-photo">
								<img src={profile?.profileImage || avatar} alt="" />
								<button type="button" className="profile-photo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} title="Change photo">
									<RiCameraLine size={15} />
								</button>
								<input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickPhoto} />
							</div>
							<div className="min-w-0">
								<div className="fs-5 fw-bold text-truncate">{`${profile?.firstName || ""} ${profile?.lastName || ""}`.trim() || "Your profile"}</div>
								<div className="text-muted small text-truncate">{profile?.email}</div>
								{uploading ? <div className="small text-muted">Uploading photo…</div> : null}
							</div>
						</div>
						<h6 className="section-title mb-3">Personal details</h6>
						<div className="row g-3 mb-3">
							<div className="col-sm-6">
								<label className="form-label">First name</label>
								<input className="form-control" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
							</div>
							<div className="col-sm-6">
								<label className="form-label">Last name</label>
								<input className="form-control" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
							</div>
							<div className="col-sm-6">
								<label className="form-label">Email</label>
								<input className="form-control" value={profile?.email || ""} disabled />
							</div>
							<div className="col-sm-6">
								<label className="form-label">Mobile</label>
								<input className="form-control" value={profile?.mobileNumber ? `${profile.countryCode || ""} ${profile.mobileNumber}` : ""} disabled />
							</div>
						</div>
						<button className="btn-accent-solid" disabled={saving || !dirty} onClick={save}>
							{saving ? "Saving..." : "Save changes"}
						</button>
					</div>
				</div>
				<div className="col-xl-5">
					<div className="card p-4 mb-4">
						<h6 className="section-title mb-2">Company</h6>
						<div className="kv-row">
							<span className="kv-label">Company name</span>
							<span className="kv-value">{profile?.companyName || "-"}</span>
						</div>
						<div className="kv-row">
							<span className="kv-label">Verification</span>
							<span className="kv-value">
								<span className={`status-pill ${profile?.dwollaStatus === "verified" ? "success" : "warning"}`}>{profile?.dwollaStatus || "not started"}</span>
							</span>
						</div>
						<div className="kv-row">
							<span className="kv-label">Wallet balance</span>
							<span className="kv-value">{profile?.dwollaBalance != null ? formatMoney(profile.dwollaBalance) : "-"}</span>
						</div>
					</div>
					<div className="card">
						{LINKS.map((l) => (
							<Link key={l.to} to={l.to} className="settings-link">
								<span className="settings-link-icon">
									<l.icon size={18} />
								</span>
								<span className="flex-grow-1 min-w-0">
									<span className="d-block fw-semibold">{l.label}</span>
									{l.text ? <span className="d-block text-muted small">{l.text}</span> : null}
								</span>
								<RiArrowRightSLine size={18} className="text-muted" />
							</Link>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
